import React, { useState } from 'react';
import { Users, UserPlus, UserCheck, Search, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { mockConnections } from '../data/mock';
import { toast } from '../hooks/use-toast';

const MyNetwork = () => {
  const [connections, setConnections] = useState(mockConnections);
  const [searchQuery, setSearchQuery] = useState('');

  const peopleYouMayKnow = [
    {
      id: "10",
      name: "Jennifer Martinez",
      title: "Training Operations Manager",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=50&h=50&fit=crop&crop=face",
      mutualConnections: 18,
      isConnected: false,
      company: "Global Training Solutions"
    },
    {
      id: "11",
      name: "Robert Chen",
      title: "Senior Instructional Designer",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face",
      mutualConnections: 12,
      isConnected: false,
      company: "TechEd Innovations"
    },
    {
      id: "12",
      name: "Sarah Williams",
      title: "Corporate Learning Consultant",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face",
      mutualConnections: 27,
      isConnected: false,
      company: "Learning Excellence Group"
    }
  ];

  const handleConnect = (personId) => {
    setConnections(prev => 
      prev.map(person => 
        person.id === personId 
          ? { ...person, isConnected: true }
          : person
      )
    );
    
    toast({
      title: "Connection sent!",
      description: "Your connection request has been sent.",
    });
  };

  const handleRemoveConnection = (personId) => {
    setConnections(prev => 
      prev.map(person => 
        person.id === personId 
          ? { ...person, isConnected: false }
          : person
      )
    );
    
    toast({
      title: "Connection removed",
      description: "You are no longer connected.",
    });
  };

  const filteredConnections = connections.filter(person =>
    person.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    person.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-lg">
            <Users className="w-6 h-6 mr-3 text-blue-600" />
            My Network
          </CardTitle>
          <p className="text-sm text-gray-600">
            Manage your professional connections and grow your training network
          </p>
        </CardHeader>
      </Card>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search your connections..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" className="flex items-center">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="connections" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="connections">
            My Connections ({connections.filter(c => c.isConnected).length})
          </TabsTrigger>
          <TabsTrigger value="discover">
            People You May Know
          </TabsTrigger>
          <TabsTrigger value="requests">
            Connection Requests (3)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="connections" className="space-y-4">
          {filteredConnections.filter(person => person.isConnected).length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No connections found</h3>
                <p className="text-gray-600 mb-4">Start building your professional network by connecting with trainers and L&D professionals.</p>
                <Button>Discover People</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredConnections.filter(person => person.isConnected).map((person) => (
                <Card key={person.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <Avatar className="w-16 h-16">
                          <AvatarImage src={person.avatar} alt={person.name} />
                          <AvatarFallback>{person.name.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <h4 className="font-semibold text-lg hover:text-blue-600 cursor-pointer">
                            {person.name}
                          </h4>
                          <p className="text-gray-600 mb-1">{person.title}</p>
                          <p className="text-sm text-gray-500">
                            {person.mutualConnections} mutual connections
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm">
                          Message
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleRemoveConnection(person.id)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="discover" className="space-y-4">
          <div className="grid gap-4">
            {peopleYouMayKnow.map((person) => (
              <Card key={person.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <Avatar className="w-16 h-16">
                        <AvatarImage src={person.avatar} alt={person.name} />
                        <AvatarFallback>{person.name.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <h4 className="font-semibold text-lg hover:text-blue-600 cursor-pointer">
                          {person.name}
                        </h4>
                        <p className="text-gray-600 mb-1">{person.title}</p>
                        <p className="text-sm text-gray-500 mb-1">{person.company}</p>
                        <p className="text-sm text-gray-500">
                          {person.mutualConnections} mutual connections
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleConnect(person.id)}
                        className="flex items-center"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Connect
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardContent className="p-8 text-center">
              <UserCheck className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">Connection Requests</h3>
              <p className="text-gray-600 mb-4">
                You have 3 pending connection requests from other training professionals.
              </p>
              <Badge variant="secondary" className="mb-4">Feature Coming Soon</Badge>
              <p className="text-sm text-gray-500">
                This feature will be available in the next update.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MyNetwork;